/**
 * Stats `/stats` — the public platform numbers, served by the `public_stats` +
 * `public_llm_stats` views (migration 0017; anon PostgREST read through
 * services.publicStats()). Headline tiles up top, then the AI bill itemized
 * three ways: by vendor, by model, and by activity. One fetch on mount — the
 * views aggregate server-side, nothing streams.
 */
import { useEffect, useMemo, useState } from 'react';
import { useServices } from '../services/context.js';
import { Mascot } from '../components/Mascot.js';
import type { PublicLlmStatRow, PublicStats } from '../services/types.js';

/** 12345678 → "12,345,678" (tiles want the full glory of every digit). */
const wholeNumber = new Intl.NumberFormat('en-US');
/** 12345678 → "12.3M" (token columns want compact). */
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

function usd(v: number): string {
  if (v >= 100) return `$${wholeNumber.format(Math.round(v))}`;
  return `$${v.toFixed(2)}`;
}

/** Vendor ids → display names (the `provider` column of llm_requests). */
const VENDOR_LABELS: Record<string, string> = {
  anthropic: 'Anthropic · Claude',
  gemini: 'Google · Gemini',
  openai: 'OpenAI · GPT',
};

/** Activity ids (`llm_requests.task`) → what a visitor calls them. */
const ACTIVITY_LABELS: Record<string, string> = {
  build: 'Game creation',
  iterate: 'Game iteration',
  amend: 'Iterate classifier',
  review: 'Safety review',
  concept: 'Concept analysis',
  asset: 'Asset generation',
  cover: 'Cover art',
};

interface LlmGroup {
  key: string;
  label: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
}

/** Fold the provider×model×task rows along one dimension. */
function groupLlm(
  rows: PublicLlmStatRow[],
  keyOf: (r: PublicLlmStatRow) => string,
  labelOf: (key: string) => string,
): LlmGroup[] {
  const byKey = new Map<string, LlmGroup>();
  for (const r of rows) {
    const key = keyOf(r);
    const g = byKey.get(key) ?? {
      key,
      label: labelOf(key),
      calls: 0,
      inputTokens: 0,
      outputTokens: 0,
      estimatedCostUsd: 0,
    };
    g.calls += r.calls;
    g.inputTokens += r.inputTokens;
    g.outputTokens += r.outputTokens;
    g.estimatedCostUsd += r.estimatedCostUsd;
    byKey.set(key, g);
  }
  return [...byKey.values()].sort((a, b) => b.estimatedCostUsd - a.estimatedCostUsd);
}

function LlmTable({ title, groups }: { title: string; groups: LlmGroup[] }) {
  const maxCost = Math.max(...groups.map((g) => g.estimatedCostUsd), 0.000001);
  return (
    <div className="panel">
      <h2 className="panel__title">{title}</h2>
      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th></th>
              <th>Calls</th>
              <th>Tokens in</th>
              <th>Tokens out</th>
              <th>Est. cost</th>
              <th className="stat-bar-col"></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.key}>
                <td>{g.label}</td>
                <td>{wholeNumber.format(g.calls)}</td>
                <td>{compact.format(g.inputTokens)}</td>
                <td>{compact.format(g.outputTokens)}</td>
                <td>{usd(g.estimatedCostUsd)}</td>
                <td className="stat-bar-col">
                  <div className="stat-bar">
                    <div
                      className="stat-bar__fill"
                      style={{ width: `${Math.max(2, (g.estimatedCostUsd / maxCost) * 100)}%` }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatTile({ value, label, sub }: { value: number; label: string; sub?: string }) {
  return (
    <div className="stat-tile" data-testid="stat-tile">
      <div className="stat-tile__value">{wholeNumber.format(value)}</div>
      <div className="stat-tile__label">{label}</div>
      {sub && <div className="stat-tile__sub">{sub}</div>}
    </div>
  );
}

export function Stats() {
  const services = useServices();
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    services
      .publicStats()
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load stats.');
      });
    return () => {
      cancelled = true;
    };
  }, [services]);

  const byVendor = useMemo(
    () =>
      groupLlm(
        stats?.llm ?? [],
        (r) => r.provider,
        (k) => VENDOR_LABELS[k] ?? k,
      ),
    [stats],
  );
  const byModel = useMemo(
    () =>
      groupLlm(
        stats?.llm ?? [],
        (r) => r.model,
        (k) => k,
      ),
    [stats],
  );
  const byActivity = useMemo(
    () =>
      groupLlm(
        stats?.llm ?? [],
        (r) => r.task,
        (k) => ACTIVITY_LABELS[k] ?? k,
      ),
    [stats],
  );
  const llmTotals = useMemo(
    () =>
      (stats?.llm ?? []).reduce(
        (acc, r) => ({
          calls: acc.calls + r.calls,
          tokens: acc.tokens + r.inputTokens + r.outputTokens,
          cost: acc.cost + r.estimatedCostUsd,
        }),
        { calls: 0, tokens: 0, cost: 0 },
      ),
    [stats],
  );

  return (
    <div className="container">
      <div className="gallery__hero gallery__hero--row">
        <div>
          <h1 className="gallery__title">djgg by the numbers</h1>
          <p className="gallery__subtitle">
            Live public stats, straight from the database. No vanity metrics — Deej counted
            them himself.
          </p>
        </div>
        <Mascot context="stats" size={300} quip={false} />
      </div>

      {error && <div className="error-text">{error}</div>}
      {!stats && !error && <div className="empty">Counting pigeons…</div>}

      {stats && (
        <>
          <div className="stats-grid" data-testid="stats-grid">
            <StatTile
              value={stats.totals.gamesCreated}
              label="Games created"
              sub={`plus ${wholeNumber.format(stats.totals.nativeGames)} native classics`}
            />
            <StatTile
              value={stats.totals.totalPlays}
              label="Total plays"
              sub={`${wholeNumber.format(stats.totals.gameViews)} game-page views`}
            />
            <StatTile
              value={stats.totals.multiplayerMatches}
              label="Multiplayer matches"
              sub="online, head-to-head"
            />
            <StatTile
              value={stats.totals.completedForks}
              label="Forks completed"
              sub="remixes that shipped"
            />
            <StatTile
              value={stats.totals.gameIterations}
              label="Game iterations"
              sub="builds beyond v1 that went live"
            />
            <StatTile
              value={stats.totals.assetsAttached}
              label="Assets created"
              sub={`${wholeNumber.format(stats.totals.assetVariantsGenerated)} variants generated`}
            />
            <StatTile
              value={stats.totals.totalUsers}
              label="Players & creators"
              sub="not counting the admin (or the pigeon)"
            />
            <StatTile
              value={stats.totals.uniqueSessions}
              label="Unique sessions"
              sub={`${wholeNumber.format(stats.totals.pageViews)} page views`}
            />
            <StatTile value={stats.totals.totalLikes} label="Likes" sub="hearts given" />
            <StatTile
              value={stats.totals.totalComments}
              label="Comments"
              sub="hot takes posted"
            />
          </div>

          <div className="gallery__hero" style={{ paddingTop: 8 }}>
            <h2 className="gallery__title" style={{ fontSize: 24 }}>
              The AI bill, itemized
            </h2>
            <p className="gallery__subtitle">
              Every game build, iteration, concept analysis and generated image is one or
              more model calls — {wholeNumber.format(llmTotals.calls)} calls,{' '}
              {compact.format(llmTotals.tokens)} tokens, ~{usd(llmTotals.cost)} at list
              pricing so far. Image generation is estimated at the model's per-image
              billing rate.
            </p>
          </div>

          {stats.llm.length === 0 ? (
            <div className="empty">No model calls recorded yet. The machines rest.</div>
          ) : (
            <>
              <LlmTable title="By vendor" groups={byVendor} />
              <LlmTable title="By model" groups={byModel} />
              <LlmTable title="By activity" groups={byActivity} />
            </>
          )}
        </>
      )}
    </div>
  );
}
